/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import styled from 'styled-components';
import appTheme from '../theme/appTheme.json';

// Service Tiers SLA Chart Color Scheme
const CLOSE_BUTTON_COLOR = appTheme.primaryTextColor;
const CLOSE_BUTTON_HOVER_COLOR = appTheme.grayTextColor;
const TABLE_CELL_BORDER_COLOR = appTheme.grayTextColor;
const TABLE_CELL_TEXT_COLOR = appTheme.primaryTextColor;
const TABLE_HEADER_AND_FOOTER_BACKGROUND_COLOR = appTheme.primaryBackgroundColor;
const TABLE_HEADER_AND_FOOTER_TEXT_COLOR = appTheme.primaryTextColor;
const TABLE_TITLE_BORDER_COLOR = appTheme.primaryTextColor;
const ODD_COLORED_ROW_BACKGROUND_COLOR = appTheme.primaryBackgroundColor;
const EVEN_COLORED_ROW_BACKGROUND_COLOR = appTheme.mptRowHoverBackground;
export interface ServiceTiersSlaChartProps {
  open: boolean;
  onClose: () => void;
}

const ServiceTiersSlaChart = (props: ServiceTiersSlaChartProps) => {
  const { open, onClose } = props;

  if (!open) {
    return null;
  }
  return (
    <PopupOverlay
      closeButtonColor={CLOSE_BUTTON_COLOR}
      closeButtonHoverColor={CLOSE_BUTTON_HOVER_COLOR}
      tableCellBorderColor={TABLE_CELL_BORDER_COLOR}
      tableCellTextColor={TABLE_CELL_TEXT_COLOR}
      tableHeaderAndFooterBackgroundColor={TABLE_HEADER_AND_FOOTER_BACKGROUND_COLOR}
      tableHeaderAndFooterTextColor={TABLE_HEADER_AND_FOOTER_TEXT_COLOR}
      tableTitleBorderColor={TABLE_TITLE_BORDER_COLOR}
      oddColoredRowBackgroundColor={ODD_COLORED_ROW_BACKGROUND_COLOR}
      evenColoredRowBackgroundColor={EVEN_COLORED_ROW_BACKGROUND_COLOR}
    >
      <div id="tiers-table-container">
        <table id="tiers-table">
          <i className="material-icons close-overlay" onClick={onClose}>
            close
          </i>
          <tbody className="table-border">
            <tr>
              <td colSpan={20} className="bold-text-cell title-cell">
                <p>Coverage Region Forward Link (FL) and Return Link (RL) Rates (Mbps)</p>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="bold-text-cell">
                <p>SIMOP Service Tier</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>CONUS</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>MEXCCA</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>NAOR</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>Covered Atlantic</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>Europe</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>E Med/E Africa/India</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>Middle East</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>Pacific</p>
              </td>
              <td colSpan={2} className="bold-text-cell">
                <p>Rest of World</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>Description</p>
              </td>
              <td className="bold-text-cell">
                <p>Band</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
              <td>
                <p>FL</p>
              </td>
              <td>
                <p>RL</p>
              </td>
            </tr>
            <tr className="striped">
              <td rowSpan={3} className="bold-text-cell">
                <p>HCX*</p>
              </td>
              <td rowSpan={3} className="bold-text-cell">
                <p>MIL-Ka</p>
              </td>
              <td colSpan={18}>
                <p>Single Terminal (Aggregate): 75Mbps</p>
              </td>
            </tr>
            <tr className="striped">
              <td colSpan={18}>
                <p>x2 Terminals (Aggregate): 45-60Mbps (primary), 10-30Mbps (secondary)</p>
              </td>
            </tr>
            <tr className="striped">
              <td colSpan={18}>
                <p>x3 Terminals (Aggregate): 35-45Mbps (primary), 10-30Mbps (secondaries)</p>
              </td>
            </tr>
            <tr className="striped">
              <td rowSpan={2} className="bold-text-cell">
                <p>KuKa Plus</p>
              </td>
              <td className="bold-text-cell">
                <p>Ka**</p>
              </td>
              <td>
                <p>20-60</p>
              </td>
              <td>
                <p>3-4.5</p>
              </td>
              <td colSpan={16}>
                <p>20 Mbps FL and 3 Mbps RL where available</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>Ku***</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>3-4</p>
              </td>
              <td>
                <p>4-10</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4-6</p>
              </td>
              <td>
                <p>1-3</p>
              </td>
              <td>
                <p>4-6</p>
              </td>
              <td>
                <p>1-3</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>3-4</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-3</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>3-4</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-4</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  Priority-Ku
                  <br />
                  FISA I,II
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ku</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4-10</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4-6</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4-6</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4-15</p>
              </td>
              <td>
                <p>1-2</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  Priority-Ku
                  <br />
                  VR-12H
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ku</p>
              </td>
              <td>
                <p>4-10</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4-10</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4-6</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4-10</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4-6</p>
              </td>
              <td>
                <p>0.5-1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  VGN Ku Premium
                  <br />
                  4Mx1M
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ku</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  VGN Ku Priority
                  <br />
                  2Mx512k
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ku</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
              <td>
                <p>2</p>
              </td>
              <td>
                <p>0.5</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  MMT Plan
                  <br />
                  4Mx1M
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ku</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  MMT Plan
                  <br />
                  15Mx3M
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ka</p>
              </td>
              <td>
                <p>15</p>
              </td>
              <td>
                <p>3</p>
              </td>
              <td colSpan={16}>
                <p>4-10 Mbps FL and 3 Mbps RL where available</p>
              </td>
            </tr>
            <tr className="striped">
              <td className="bold-text-cell">
                <p>
                  VGN Ku Premium
                  <br />
                  Enhanced 10Mx1M
                </p>
              </td>
              <td className="bold-text-cell">
                <p>Ku</p>
              </td>
              <td>
                <p>10</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>10</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>10</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
              <td>
                <p>4</p>
              </td>
              <td>
                <p>1</p>
              </td>
            </tr>
            <tr>
              <td id="info-text" colSpan={20}>
                <p>* HCX rates are provided at best effort below 10 degrees elevation look angle (footprint edge)</p>
                <p>** Ka-band is the primary capacity where available</p>
                <p>
                  *** Ku-band data rates represent a failover capacity when a primary Ka-band capacity is not available
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </PopupOverlay>
  );
};

export default ServiceTiersSlaChart;

interface PopupOverlayProps {
  closeButtonColor: string;
  closeButtonHoverColor: string;
  tableCellBorderColor: string;
  tableCellTextColor: string;
  tableHeaderAndFooterBackgroundColor: string;
  tableHeaderAndFooterTextColor: string;
  tableTitleBorderColor: string;
  oddColoredRowBackgroundColor: string;
  evenColoredRowBackgroundColor: string;
}

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.5); // Surrounding Popup Color

  .close-overlay {
    top: 18px;
    right: 8px;
    position: absolute;
    color: ${({ closeButtonColor }: PopupOverlayProps) => closeButtonColor}; // Close Button Color
    font-weight: bold;
  }

  .close-overlay:hover,
  .close-overlay:focus {
    color: ${({ closeButtonHoverColor }: PopupOverlayProps) => closeButtonHoverColor}; // Close Button Hover Color
    text-decoration: none;
    cursor: pointer;
  }

  /* Service tiers table */
  #tiers-table-container {
    overflow: auto;
  }

  .table-border {
    border: solid 16px
      ${({ tableHeaderAndFooterBackgroundColor }: PopupOverlayProps) => tableHeaderAndFooterBackgroundColor};
  }

  #tiers-table {
    border-collapse: collapse;
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    margin: 10vh auto 0;
    max-width: 700px;
  }

  #tiers-table td {
    border: solid ${({ tableCellBorderColor }: PopupOverlayProps) => tableCellBorderColor} 1pt; // Table Cell Border Color
    color: ${({ tableCellTextColor }: PopupOverlayProps) => tableCellTextColor}; // Table Cell Text Color
  }

  #tiers-table p {
    margin: 0.5em 1em;
    text-align: center;
    white-space: nowrap;
  }

  #tiers-table td#info-text,
  #tiers-table td#info-text p {
    background: ${({ tableHeaderAndFooterBackgroundColor }: PopupOverlayProps) =>
      tableHeaderAndFooterBackgroundColor} !important; // Table Footer Background Color
    color: ${({ tableHeaderAndFooterTextColor }: PopupOverlayProps) =>
      tableHeaderAndFooterTextColor}; // Table Footer Text Color
    text-align: left;
  }

  #tiers-table .bold-text-cell,
  #tiers-table .bold-text-cell p {
    background: ${({ tableHeaderAndFooterBackgroundColor }: PopupOverlayProps) =>
      tableHeaderAndFooterBackgroundColor} !important; // Table Header Background Color
    color: ${({ tableHeaderAndFooterTextColor }: PopupOverlayProps) =>
      tableHeaderAndFooterTextColor}; // Table Header Text Color
    font-weight: bold;
    text-align: center;
  }

  #tiers-table .title-cell {
    border-bottom: solid ${({ tableTitleBorderColor }: PopupOverlayProps) => tableTitleBorderColor} 3pt; // Table Title Border Color
  }

  // Remove outer borders
  #tiers-table tr:first-child td {
    border-top: 0;
  }
  #tiers-table tr td:first-child {
    border-left: 0;
  }
  #tiers-table tr:last-child td {
    border-bottom: 0;
  }
  #tiers-table tr td:last-child {
    border-right: 0;
  }

  // Striped rows
  #tiers-table tr.striped:nth-child(odd) {
    background: ${({ oddColoredRowBackgroundColor }: PopupOverlayProps) =>
      oddColoredRowBackgroundColor}; // Odd Colored Row Background Color
  }
  #tiers-table tr.striped:nth-child(even) {
    background: ${({ evenColoredRowBackgroundColor }: PopupOverlayProps) =>
      evenColoredRowBackgroundColor}; // Even Colored Row Background Color
  }
`;
